import { Platform } from 'react-native';
import { DEFAULT_CUSTOMER_QUEUE_VISIBILITY, type CustomerQueueVisibility } from '../types/customerQueueVisibility';
import {
  parseCustomerAppointmentBusinessPrefs,
  type CustomerAppointmentBusinessPrefs,
} from '../types/customerPickupSettings';

function snapshotData(snap: { exists?: boolean | (() => boolean); data?: () => Record<string, unknown> } | null): Record<string, unknown> | null {
  if (!snap) return null;
  const ok = typeof snap.exists === 'function' ? (snap.exists as () => boolean)() : !!snap.exists;
  if (!ok) return null;
  return (snap.data?.() ?? null) as Record<string, unknown> | null;
}

const defaultPrefs = (): CustomerAppointmentBusinessPrefs =>
  parseCustomerAppointmentBusinessPrefs(null);

/**
 * Reads queue visibility + pickup/SMS prefs from each `businesses/{id}` doc (one getDoc per id).
 */
export async function fetchCustomerAppointmentBusinessPrefs(
  businessIds: string[]
): Promise<Record<string, CustomerAppointmentBusinessPrefs>> {
  const unique = [...new Set(businessIds.filter(Boolean))];
  const out: Record<string, CustomerAppointmentBusinessPrefs> = {};
  if (unique.length === 0) return out;

  const fillDefaults = () => {
    unique.forEach((id) => {
      out[id] = defaultPrefs();
    });
  };

  if (Platform.OS === 'web') {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
      if (!initializeFirebase()) {
        fillDefaults();
        return out;
      }
      const db = getWebDb();
      if (!db) {
        fillDefaults();
        return out;
      }
      await Promise.all(
        unique.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'businesses', id));
            const data = snapshotData(snap as any);
            out[id] = parseCustomerAppointmentBusinessPrefs(data ?? undefined);
          } catch {
            out[id] = defaultPrefs();
          }
        })
      );
    } catch {
      fillDefaults();
    }
    return out;
  }

  try {
    const { initializeFirebase } = await import('./unifiedAuthService');
    initializeFirebase();
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, getDoc } = require('@react-native-firebase/firestore');
    const app = getApp();
    const db = getFirestore(app);
    await Promise.all(
      unique.map(async (id: string) => {
        try {
          const snap = await getDoc(doc(db, 'businesses', id));
          const data = snapshotData(snap as any);
          out[id] = parseCustomerAppointmentBusinessPrefs(data ?? undefined);
        } catch {
          out[id] = defaultPrefs();
        }
      })
    );
  } catch {
    fillDefaults();
  }
  return out;
}

/**
 * @deprecated Prefer `fetchCustomerAppointmentBusinessPrefs` when pickup prefs are needed.
 */
export async function fetchCustomerQueueVisibilities(
  businessIds: string[]
): Promise<Record<string, CustomerQueueVisibility>> {
  const prefs = await fetchCustomerAppointmentBusinessPrefs(businessIds);
  const out: Record<string, CustomerQueueVisibility> = {};
  const unique = [...new Set(businessIds.filter(Boolean))];
  for (const id of unique) {
    out[id] = prefs[id]?.customerQueueVisibility ?? DEFAULT_CUSTOMER_QUEUE_VISIBILITY;
  }
  return out;
}
